import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Search,
  Info,
  Calculator,
  PoundSterling,
  Truck,
  Package,
  CreditCard,
  XCircle,
  Phone,
  ArrowUpRight,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ICONS = {
  General: Info,
  "Quotes & Bookings": Calculator,
  Pricing: PoundSterling,
  "Moving Day": Truck,
  "Delivery Services": Package,
  Payments: CreditCard,
  Cancellations: XCircle,
  Contact: Phone,
};

const FAQS = [
  {
    category: "General",
    items: [
      {
        q: "What services do you provide?",
        a: (
          <>
            Jaybliss Logistics provides professional logistics services including:
            <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 list-disc pl-5">
              <li>Home Removals</li>
              <li>Office Relocations</li>
              <li>Man &amp; Van Services</li>
              <li>Furniture Delivery</li>
              <li>Van Hire</li>
              <li>Packing &amp; Unpacking</li>
              <li>Student Moves</li>
              <li>Single Item Deliveries</li>
            </ul>
          </>
        ),
        search:
          "home removals office relocations man van furniture delivery hire packing unpacking student single item",
      },
      {
        q: "Which areas do you cover?",
        a: "We are based in Belfast, Northern Ireland and provide services throughout Northern Ireland. Long-distance and UK mainland moves are also available upon request.",
      },
      {
        q: "Are you fully insured?",
        a: "Yes. We are fully insured, giving you peace of mind that your belongings are protected throughout your move.",
      },
    ],
  },
  {
    category: "Quotes & Bookings",
    items: [
      {
        q: "How do I get a quote?",
        a: "Simply complete our online quote form or contact us by phone or email. We'll provide a personalised quotation based on your requirements.",
      },
      {
        q: "Is getting a quote free?",
        a: "Yes. All quotations are completely free with no obligation.",
      },
      {
        q: "How is my quote calculated?",
        a: (
          <>
            Our quotes are based on:
            <ul className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5 list-disc pl-5">
              <li>Distance</li>
              <li>Number of items</li>
              <li>Property access</li>
              <li>Number of movers required</li>
              <li>Packing requirements</li>
              <li>Time required</li>
            </ul>
          </>
        ),
        search: "distance items access movers packing time",
      },
    ],
  },
  {
    category: "Pricing",
    items: [
      {
        q: "What are your starting prices?",
        a: (
          <>
            <ul className="grid gap-1.5 list-disc pl-5">
              <li>Single Furniture Delivery — From £25</li>
              <li>Man &amp; Van Service — From £40/hour</li>
              <li>2 Men &amp; Van Service — From £60/hour</li>
            </ul>
            <p className="mt-3">Final pricing depends on your move requirements.</p>
          </>
        ),
        search: "£25 £40 £60 furniture man van hour starting",
      },
      {
        q: "Are there any hidden charges?",
        a: "No. We believe in transparent pricing. Any additional costs will always be discussed before your booking is confirmed.",
      },
    ],
  },
  {
    category: "Moving Day",
    items: [
      {
        q: "Can you help with loading and unloading?",
        a: "Yes. Our team will safely load and unload your belongings.",
      },
      {
        q: "Can I travel in the van?",
        a: "This depends on the type of booking and insurance requirements. Please ask when making your booking.",
      },
    ],
  },
  {
    category: "Delivery Services",
    items: [
      {
        q: "Do you deliver single furniture items?",
        a: "Yes. We collect and deliver single furniture items purchased from shops, marketplaces, or private sellers.",
      },
      {
        q: "Do you offer same-day delivery?",
        a: "Yes, subject to availability.",
      },
    ],
  },
  {
    category: "Payments",
    items: [
      {
        q: "What payment methods do you accept?",
        a: (
          <>
            We accept:
            <ul className="mt-3 grid gap-1.5 list-disc pl-5">
              <li>Bank Transfer</li>
              <li>Debit &amp; Credit Cards</li>
              <li>Cash</li>
            </ul>
          </>
        ),
        search: "bank transfer debit credit cards cash payment",
      },
      {
        q: "When do I pay?",
        a: "Payment is normally made once the job has been completed unless agreed otherwise.",
      },
    ],
  },
  {
    category: "Cancellations",
    items: [
      {
        q: "Can I cancel or reschedule?",
        a: "Yes. Please contact us as soon as possible if your plans change.",
      },
    ],
  },
  {
    category: "Contact",
    items: [
      {
        q: "How can I contact Jaybliss Logistics?",
        a: (
          <div className="space-y-1">
            <div><b>Phone:</b> <a href="tel:+447396764997" className="link-underline">07396 764997</a></div>
            <div><b>Email:</b> <a href="mailto:info@jayblisslogistics.co.uk" className="link-underline">info@jayblisslogistics.co.uk</a></div>
            <div><b>Location:</b> Belfast, Northern Ireland</div>
          </div>
        ),
        search: "phone email location contact belfast 07396 764997",
      },
    ],
  },
];

const strOf = (a) => (typeof a === "string" ? a : "");

export default function FAQ() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQS;
    return FAQS.map((section) => ({
      ...section,
      items: section.items.filter(
        (it) =>
          it.q.toLowerCase().includes(q) ||
          strOf(it.a).toLowerCase().includes(q) ||
          (it.search || "").toLowerCase().includes(q) ||
          section.category.toLowerCase().includes(q)
      ),
    })).filter((s) => s.items.length > 0);
  }, [query]);

  return (
    <section id="faq" className="bg-[var(--jb-bone)] py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* LEFT — heading + search */}
          <div className="md:col-span-4 md:sticky md:top-24 h-fit">
            <div className="mono-cap text-[var(--jb-rust)] mb-4">(FAQ)</div>
            <h2 className="serif text-[clamp(36px,5vw,72px)] leading-[0.98] tracking-tight text-[var(--jb-navy)]">
              Everything <br />
              <span className="italic">you might ask.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-[1.6] text-[var(--jb-muted)] max-w-[400px]">
              Can't find the answer? Give us a ring on <b>07396 764997</b> — we're happy to help.
            </p>

            <div className="mt-8 relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[var(--jb-muted)]" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs…"
                className="w-full bg-white border border-[var(--jb-line)] rounded-lg pl-11 pr-4 py-3.5 text-[15px] text-[var(--jb-navy)] placeholder:text-[var(--jb-muted)] focus:outline-none focus:border-[var(--jb-rust)] focus:ring-2 focus:ring-[var(--jb-rust)]/15 shadow-sm transition-all"
                data-testid="faq-search"
                aria-label="Search frequently asked questions"
              />
            </div>
            <div className="mt-3 mono-cap text-[10px] text-[var(--jb-muted)]" aria-live="polite">
              {filtered.reduce((n, s) => n + s.items.length, 0)} results
            </div>
          </div>

          {/* RIGHT — accordion */}
          <div className="md:col-span-8">
            {filtered.length === 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white rounded-2xl border border-[var(--jb-line)] p-10 text-center shadow-sm"
              >
                <div className="serif text-2xl text-[var(--jb-navy)]">No matches.</div>
                <p className="mt-2 text-[14px] text-[var(--jb-muted)]">
                  Try a different phrase — or call us on 07396 764997.
                </p>
              </motion.div>
            ) : (
              <div className="space-y-10">
                {filtered.map((section, si) => {
                  const Icon = ICONS[section.category] || Info;
                  return (
                    <motion.div
                      key={section.category}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-60px" }}
                      transition={{ duration: 0.6, delay: si * 0.05 }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-9 h-9 rounded-full bg-[var(--jb-navy)] text-[var(--jb-rust)] grid place-items-center">
                          <Icon className="w-4 h-4" strokeWidth={1.5} />
                        </div>
                        <h3 className="mono-cap text-[var(--jb-navy)]">{section.category}</h3>
                      </div>

                      <Accordion
                        type="single"
                        collapsible
                        className="bg-white rounded-2xl border border-[var(--jb-line)] shadow-sm overflow-hidden"
                      >
                        {section.items.map((item, i) => {
                          const id = `${section.category}-${i}`;
                          return (
                            <AccordionItem
                              key={id}
                              value={id}
                              className="border-b border-[var(--jb-line)] last:border-b-0"
                            >
                              <AccordionTrigger
                                className="px-6 py-5 hover:no-underline text-left [&[data-state=open]>span]:text-[var(--jb-rust)]"
                                data-testid={`faq-q-${id}`}
                              >
                                <span className="serif text-[18px] md:text-[20px] text-[var(--jb-navy)] leading-snug pr-4 transition-colors">
                                  {item.q}
                                </span>
                              </AccordionTrigger>
                              <AccordionContent className="px-6 pb-6 pt-1 text-[15px] leading-[1.65] text-[var(--jb-muted)]">
                                {item.a}
                              </AccordionContent>
                            </AccordionItem>
                          );
                        })}
                      </Accordion>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7 }}
              className="mt-14 bg-[var(--jb-navy)] text-[var(--jb-bone)] rounded-2xl p-8 md:p-10 relative overflow-hidden shadow-sm"
              data-testid="faq-cta"
            >
              <div className="grain absolute inset-0 pointer-events-none" />
              <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                  <div className="mono-cap text-[var(--jb-rust)] mb-2">Still have questions?</div>
                  <div className="serif text-2xl md:text-3xl leading-tight">
                    Get a free, no-obligation quote in <span className="italic">under a minute.</span>
                  </div>
                </div>
                <a
                  href="#quote"
                  className="cta-primary bg-[var(--jb-rust)] text-[var(--jb-bone)] mono-cap px-7 py-4 inline-flex items-center gap-2 self-start md:self-auto whitespace-nowrap"
                  data-testid="faq-cta-btn"
                >
                  Request a Quote <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
